import type { ConnectionCapabilityEnum } from 'fleet-data-model';
import {
  AvailablePatternIconEnum,
  type AssetDetails,
  type MachineData,
  type Status,
} from '@fleet-bffs/api-contract';
import type { DayName } from '~/domain/roboticmower-details/store/schedule/day-names';
import type { CreateAccessGroupResponse } from '~/api/access-groups-api/types';
import type AssetTypeConstants from '~/constants/asset-type-constants';
import type AutomowerStatusConstantsV3 from '~/constants/automower-status-constants-v3';
import type FetchStatusConstants from '~/constants/fetch-status-constants';
import type FirmwareTargetConstants from '~/constants/firmware-target-constants';
import type FirmwareUpdateStatusConstants from '~/constants/firmware-update-status-constants';
import type GatewayTypeConstants from '~/constants/gateway-type-constants';
import type GeneralAccessoriesConnectionStatusConstants from '~/constants/general-accessories-connection-status-constants';
import type IconPlacementConstants from '~/constants/icon-placement-constants';
import type IconTypeConstants from '~/constants/icon-type-constants';
import type OtherMachineStatusConstants from '~/constants/other-machine-status-constants';
import type { RelationshipTagCategory } from '~/ducks/asset-tag-relationships/types';
import type { MaintenanceReminder } from '~/data/MaintenanceReminder';
import type RoboticMowerCommand from '~/ducks/robotic-mower-command/robotic-mower-command';
import type RoboticMowerSetting from '~/ducks/robotic-mower-command/robotic-mower-setting';
import type {
  ProductBatteryTypes,
  ProductBatteryHealthStates,
} from '~/constants/product-battery-constants';

export { AvailablePatternIconEnum } from '@fleet-bffs/api-contract';

export interface Asset {
  activated: boolean;
  deactivationPossible: boolean;
  assetDetails: AssetDetailsFields | AssetDetails;
  battery: AssetBattery | null;
  assetInformations: AssetInformationFields[];
  canHaveSensor: boolean;
  deletable: boolean;
  showMachineUsageGraph: boolean;
  icons: IconType[];
  inventoryStatus: InventoryStatusFields | null;
  location: LocationFields | null; //Includes record (location.position === LatLngRecord)
  machineData?: MachineData;
  machineUsageTotalRunningTime: TotalRunningTimeFields | null;
  maintenanceReminder: MaintenanceReminder;
  ownershipData: MachineOwnershipData;
  roboticMower: RoboticMower | null; //Includes multiple records
  serviceContract: ServiceContractFields | null;
  firmwareUpdate: FirmwareUpdateType | null;
  firmware: FirmwareVersionType | null;
  connectivityIcons: IconType[];
  tags: RelationshipTagCategory[];
  accessGroups: CreateAccessGroupResponse[];
  assetErrorSeverity: AssetErrorSeverity;
  status?: Status;
}

export interface AssetBattery {
  percentage: number;
  type: ProductBatteryTypes;
  healthState: ProductBatteryHealthStates | null;
  healthStateExtraInformation?: string;
  timestamp: number;
  totalChargeCycles: number | null;
  totalChargeEnergy: number | null;
  batteryHealth: number;
  assetErrorSeverity: AssetErrorSeverity;
}

export enum AssetErrorSeverity {
  Caution = 'CAUTION',
  Error = 'ERROR',
  Normal = 'NORMAL',
}

export interface AvailablePattern {
  cuttingPatternIconType: AvailablePatternIconEnum;
  cuttingPatternTranslationKey: string;
  orientationShift: number;
}

export interface RoboticMowerAsset extends Asset {
  roboticMower: RoboticMower;
  inventoryStatus: null;
  machineUsageTotalRunningTime: null;
  serviceContract: null;
}

export interface NotRoboticMowerAsset extends Asset {
  inventoryStatus: InventoryStatusFields | null;
  machineUsageTotalRunningTime: TotalRunningTimeFields;
  roboticMower: null;
  serviceContract: ServiceContractFields | null;
  showCurrentlyRaisedAlarms: boolean;
}

export interface GeneralAccessoryAsset extends NotRoboticMowerAsset {
  connectionStatus: ConnectionStatusFields | null;
}

export interface AssetDetailsFields {
  id: string;
  fmsSensorId?: string;
  articleNumber: string;
  assetType: AssetTypeConstants;
  brand: string;
  category: string;
  images: AssetImageFields;
  model: string;
  name: string;
  serialNumber: string;
  manufacturedDate?: string;
  subcategory: string;
  subcategoryName: string;
  connectionCapabilities?: ConnectionCapabilityEnum[];
}

export interface AssetImageFields {
  smallImage: string;
  largeImage: string;
  subcategoryIcon?: string;
}

export interface AssetInformationFields {
  langKey: string;
  langKeyMore: string;
}

export interface FirmwareVersionType {
  version: string;
  date: string | null;
  target: FirmwareTargetConstants;
}

export enum FirmwareUnavailableReason {
  LOW_BATTERY = 'LOW_BATTERY',
  NOT_IN_CHARGING_STATION = 'NOT_IN_CHARGING_STATION',
}

export interface FirmwareUpdateType {
  approved: boolean;
  status: FirmwareUpdateStatusConstants;
  statusTranslationKey: string;
  firmwareUpdateUnavailableReasons: FirmwareUnavailableReason[];
}

export interface LocationFields {
  latitude: string;
  longitude: string;
  locationName: string | null;
  gatewayName: string;
  placeId?: string;
  gatewayType: GatewayTypeConstants | undefined;
  statusTime: LocationStatusTimeFields | undefined;
  timezoneId?: string;
  reportedByDevice?: boolean;
}

export interface LocationStatusTimeFields {
  statusTimeAsDate: boolean;
  statusTimeUnit: string;
  statusTimeValue: string;
}

export interface ServiceContractFields {
  fromDate: string;
  toDate: string;
  statusTranslationKey: string;
}

export interface TotalRunningTimeFields {
  fetchStatus: FetchStatusConstants;
  hoursOfTotalRunningTime: number;
}

export interface IconType {
  type: IconTypeConstants;
  iconPositions: Array<IconPlacementConstants>;
  data: { [key: string]: string | number | boolean | TaskList };
  title: { [key: string]: string | number };
  langKey?: string;
}

export interface MachineOwnershipData {
  purchaseCost: number;
  purchaseDate: string | null;
  estimatedLifeTimeEngineRunningTimeInHours: number;
  estimatedLifeTimeDate?: string | null;
}

export enum BorderMowingType {
  VARIABLE = 'VARIABLE',
  FIXED = 'FIXED',
}

export enum MowingMode {
  RANDOM = 'HIREP_RANDOM',
  SYSTEMATIC = 'HIREP_SYSTEMATIC',
}

export enum MowingPriority {
  AREA_FIRST = 'AREA_FIRST',
  BOUNDARY_FIRST = 'BOUNDARY_FIRST',
}

export interface RoboticMowerSettings {
  /** No longer read from the asset; mower cutting height comes from the HFS action. */
  cuttingHeight?: CuttingHeightSetting;
  /** No longer read from the asset; the mower schedule comes from the HFS action. */
  schedule?: {
    desiredSchedule: any;
    taskList: TaskList;
  };
  fairwayCutting: FairwayCuttingSetting;
  supportMultipleAreas?: boolean;
}

export interface MissionSettings {
  cuttingHeight: CuttingHeightSetting;
  schedule: {
    desiredSchedule: any;
    taskList: TaskList;
  };
  pattern?: PatternSetting;
  type: MowingMode;
}

export interface FairwayCuttingSetting {
  enabled: boolean | null;
}

export interface CuttingHeightSetting {
  cuttingHeight: number;
  cuttingHeightMM: number;
  cuttingHeightMMSetByUser: number;
  desiredCuttingHeight: any;
}

export interface PatternSetting {
  orientation: number;
  borderMowingType: BorderMowingType | null;
  borderMowingEnabled?: boolean;
  orientationShift: number;
  cuttingPatternIconType?: AvailablePatternIconEnum;
  cuttingPatternTranslationKey?: string;
}

export type TaskList = {
  [k in DayName]?: Task[];
};

export interface Task {
  id?: string;
  startTime: number;
  endTime: number;
  missionId?: number;
}

export interface StatusTimeFields {
  statusTimeStoredAsDate: boolean;
  statusTimeUnit: string;
  statusTimeValue: string;
}

export interface InventoryStatusFields extends StatusTimeFields {
  status: OtherMachineStatusConstants;
  translationKey: string;
  gatewayType: GatewayTypeConstants;
}

export interface ConnectionStatusFields extends StatusTimeFields {
  status: GeneralAccessoriesConnectionStatusConstants;
  translationKey: string;
}

export interface RoboticMowerStatusType extends StatusTimeFields {
  connected: boolean;
  nextErrorCodeTime: string;
  nextStartTime: string;
  type: AutomowerStatusConstantsV3;
  errorConfirmable: boolean;
  timestamp: number;
}

export interface FairwayKit {
  enabled: boolean;
  offset: number | null;
}

export interface RoboticMower {
  eposMower: boolean;
  hasFleetBleSupport: boolean;
  intendedForPro: boolean;
  hasInstallationSettings: boolean;
  ceoraMower: boolean;
  lonaScanInProgress: boolean;
  hasMaintenancePoint: boolean;
  availableSettings: RoboticMowerSetting[];
  extraInformation: string | null;
  extraInformationTroubleshoot: string | null;
  settings: RoboticMowerSettings;
  status: RoboticMowerStatusType;
  fairwayKit: FairwayKit | null;
  validCommands: RoboticMowerCommand[];
  validSettings: RoboticMowerSetting[];
  availablePatterns: AvailablePattern[];
  systematicMissionCapable: boolean;
  randomMissionCapable: boolean;
  maxNumberOfTasks: number;
  maxNumberOfTasksPerDay: number;
  assetErrorSeverity: AssetErrorSeverity;
  supportMultipleAreas: boolean;
  errorCode: number | null;
  hmiCapable?: boolean;
  chargingStationFirmwareVersion?: string;
  supportSecuritySettings: boolean;
}

export interface Mission {
  missionId: number;
  type: MowingMode;
  name: string;
  areaId: string;
  settings: MissionSettings;
  mowingPriority: MowingPriority;
  raiseHeightOnTurnEnabled?: boolean | null;
  enabled?: boolean | null;
}
